import { Chat } from "@ai-sdk/react";
import { CSRF_HEADER } from "@esi/shared";
import { DefaultChatTransport } from "ai";
import { apiFetch } from "../api.ts";
import { authorizedHistory } from "./history.ts";
import type { ChatMessage } from "./MessageView.tsx";
import { pendingDecisionOf, toolApprovalPath } from "./tool-approvals.ts";

/** Where a Revise click sends its turn: the change request's own route. */
export interface RevisionTarget {
  projectId: string;
  changeSetId: string;
  threadId: string;
}
export const revisionPath = (r: RevisionTarget) =>
  `/api/projects/${encodeURIComponent(r.projectId)}/wiki/change-sets/${encodeURIComponent(r.changeSetId)}/threads/${encodeURIComponent(r.threadId)}/revise`;

/** Where Resolve with the agent sends its turn (wiki-resolve-with-agent D2): the set's route. */
export interface ResolutionTarget {
  projectId: string;
  changeSetId: string;
}
export const resolutionPath = (r: ResolutionTarget) =>
  `/api/projects/${encodeURIComponent(r.projectId)}/wiki/change-sets/${encodeURIComponent(r.changeSetId)}/resolve`;

/** Where "Ask the agent" from a page thread sends the new conversation's first turn (D17). */
export interface AskTarget {
  projectId: string;
  pageId: string;
  threadId: string;
}
export const askPath = (a: AskTarget) =>
  `/api/projects/${encodeURIComponent(a.projectId)}/wiki/pages/${encodeURIComponent(a.pageId)}/threads/${encodeURIComponent(a.threadId)}/ask`;

/**
 * One chat per conversation, on the ID-addressed API (`/api/conversations/:id`): the server
 * keeps only the last (user) message of a request and reads the project from the stored
 * conversation.
 *
 * A decision on an outbound call uses the chat's own approval flow: `addToolApprovalResponse`
 * marks the part `approval-responded`, `sendAutomaticallyWhen` then sends it, and the request
 * goes to the decision endpoint with `{ decision }` only (the server approves the arguments it
 * stored, never the client's). Its response is a UI message stream that continues the same
 * assistant message. Each approval is sent once: a refused one (`409`) is never retried.
 */
export function createChat(
  conversationId: string,
  csrfToken: string,
  initialMessages: ChatMessage[],
  onTurnEnd: () => void,
): Chat<ChatMessage> {
  const sent = new Set<string>();
  const chat = new Chat<ChatMessage>({
    id: conversationId,
    messages: initialMessages,
    transport: new DefaultChatTransport<ChatMessage>({
      // History comes from the database, never from the client.
      api: `/api/conversations/${conversationId}/messages`,
      fetch: apiFetch,
      headers: { [CSRF_HEADER]: csrfToken },
      prepareSendMessagesRequest: ({ messages: all, body, trigger, messageId }) => {
        const decision =
          trigger === "submit-message" ? pendingDecisionOf(all, messageId, sent) : null;
        if (decision) {
          sent.add(decision.approvalId);
          return {
            api: toolApprovalPath(conversationId, decision.approvalId),
            body: { decision: decision.decision },
          };
        }
        // A Revise click (wiki-collaboration D15): the server composes the message from the
        // change request; only the picker's choice is sent.
        const revision = (body as { revision?: RevisionTarget } | undefined)?.revision;
        if (trigger === "submit-message" && revision) {
          return {
            api: revisionPath(revision),
            body: { ...(body?.options ? { options: body.options } : {}) },
          };
        }
        // Resolve with the agent (wiki-resolve-with-agent D2): the server composes the message
        // from the set's open threads and pins the turn to the set; only the picker's choice is
        // sent.
        const resolution = (body as { resolution?: ResolutionTarget } | undefined)?.resolution;
        if (trigger === "submit-message" && resolution) {
          return {
            api: resolutionPath(resolution),
            body: { ...(body?.options ? { options: body.options } : {}) },
          };
        }
        // "Ask the agent" (wiki-collaboration D17): the server composes the first message from
        // the page thread; the new conversation and the picker's choice are sent.
        const ask = (body as { ask?: AskTarget } | undefined)?.ask;
        if (trigger === "submit-message" && ask) {
          return {
            api: askPath(ask),
            body: { conversationId, ...(body?.options ? { options: body.options } : {}) },
          };
        }
        return {
          body: {
            ...body,
            messages: all.slice(-1).map((message) => ({
              ...message,
              parts: message.parts.filter((part) => part.type === "text"),
            })),
          },
        };
      },
    }),
    // Each answered approval is sent as soon as no reply is streaming, one request each.
    sendAutomaticallyWhen: ({ messages }) => pendingDecisionOf(messages, undefined, sent) !== null,
    // The title is written during the first turn: refresh lists and header once a turn ends.
    onFinish: () => onTurnEnd(),
  });
  authorizedHistory.set(chat, initialMessages);
  return chat;
}
